import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Anchor,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Loader,
  Paper,
  Popover,
  Skeleton,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import {
  IconBrandLinkedin,
  IconBrandWhatsapp,
  IconCheck,
  IconExternalLink,
  IconLink,
  IconMail,
  IconMessage,
  IconPlus,
  IconSparkles,
  IconWorld,
} from '@tabler/icons-react';
import useApi from '../../hooks/useApi';
import {
  fetchV2Destinations,
  generateTouchPage,
  patchTouch,
  writeV2CampaignMessage,
} from '../../services/api';

const CHANNEL_META = {
  whatsapp: { label: 'WhatsApp', Icon: IconBrandWhatsapp, bubble: '#DCF8C6' },
  sms: { label: 'SMS', Icon: IconMessage, bubble: '#E3F2FD' },
  email: { label: 'Email', Icon: IconMail, bubble: '#FFFFFF' },
  linkedin: { label: 'LinkedIn', Icon: IconBrandLinkedin, bubble: '#EEF2FA' },
};

const DEBOUNCE_MS = 700;

function channelMeta(channel) {
  return CHANNEL_META[channel] || { label: 'Message', Icon: IconMessage, bubble: '#FFFFFF' };
}

function roleForIndex(index, total, title) {
  if (title) return title;
  if (index === 0) return 'Hello';
  if (index === total - 1) return 'Last nudge';
  return 'Follow up';
}

function formatDay(day) {
  if (day === 0) return 'Today';
  if (day === 1) return 'Tomorrow';
  return `Day ${day}`;
}

function audiencePhrase(flow, answers) {
  const noun = flow?.audience_noun || 'people';
  // v2.0.2: location is now multi-select — read items array; fall back to legacy .text.
  const whereItems = Array.isArray(answers?.where?.items) ? answers.where.items : [];
  const whereText = whereItems.length ? whereItems.join(', ') : (answers?.where?.text || '');
  const where = whereText ? ` in ${whereText}` : '';
  return `${noun}${where}`;
}

function hasLinkToken(text) {
  return /\{\{link\}\}/i.test(String(text || ''));
}

function stripLinkToken(text) {
  return String(text || '')
    .replace(/\s*\{\{link\}\}\s*/gi, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/ {2,}/g, ' ')
    .trim();
}

function normalizeHttpUrl(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return null;
  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch (_err) {
    try {
      parsed = new URL(`https://${trimmed}`);
    } catch (_err2) {
      return null;
    }
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
  return parsed.toString();
}

function hostLabel(url) {
  try {
    return new URL(url).host;
  } catch (_err) {
    return url;
  }
}

function isHostedAviaryUrl(url) {
  return /:\/\/go\.getadmin\.ai\/p\//i.test(String(url || ''));
}

function normalisedPageKind(raw) {
  const text = String(raw || '').trim();
  if (!text) return null;
  if (['none', 'null', 'n/a', 'unknown'].includes(text.toLowerCase())) return null;
  return text;
}

function DestinationChip({
  touch,
  campaignId,
  index,
  messageValue,
  onMessageChange,
  onLocalUpdate,
  destinations,
  destinationsLoading,
  destinationsError,
  ensureDestinationsLoaded,
}) {
  const api = useApi();
  const [opened, setOpened] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | saving | generating | error
  const [statusText, setStatusText] = useState('');
  const [pasteValue, setPasteValue] = useState('');
  const currentUrl = String(touch.destinationUrl || '').trim();

  const hostedLookup = useMemo(() => {
    const map = new Map();
    for (const page of (destinations?.hostedPages || [])) {
      map.set(String(page.url || ''), page);
    }
    return map;
  }, [destinations]);

  const selectedHostedPage = hostedLookup.get(currentUrl) || null;
  const hasSavedDestination = !!currentUrl;
  const hasAviaryDestination = isHostedAviaryUrl(currentUrl);
  const pageKindHint = normalisedPageKind(touch.pageKind);

  const chipTone = status === 'error'
    ? 'red'
    : (selectedHostedPage || hasAviaryDestination)
      ? 'sage'
      : 'gray';

  let chipText = 'Add a destination \u25BE';
  if (status === 'generating') chipText = 'Drafting page...';
  else if (status === 'saving') chipText = 'Saving destination...';
  else if (status === 'error') chipText = statusText || 'Could not update destination - Retry';
  else if (selectedHostedPage) chipText = `Aviary page ready - ${selectedHostedPage.title || selectedHostedPage.slug}`;
  else if (hasSavedDestination && hasAviaryDestination) chipText = 'Aviary page ready - Change \u25BE';
  else if (hasSavedDestination) chipText = `${hostLabel(currentUrl)} - Change \u25BE`;
  else chipText = 'Where should this link go? \u25BE';

  const setDestinationUrl = useCallback(async (nextUrl) => {
    setStatus('saving');
    setStatusText('');
    try {
      const updated = await patchTouch(api, campaignId, touch.id, { destinationUrl: nextUrl });
      onLocalUpdate?.(index, updated);
      setStatus('idle');
      setOpened(false);
    } catch (err) {
      setStatus('error');
      setStatusText('Could not save destination - Retry');
      console.error(`[v2 messages] touch ${index} destination save failed:`, err);
    }
  }, [api, campaignId, touch.id, onLocalUpdate, index]);

  const handlePasteCommit = useCallback(async () => {
    if (!String(pasteValue || '').trim()) return;
    const normalized = normalizeHttpUrl(pasteValue);
    if (!normalized) {
      setStatus('error');
      setStatusText('Enter a valid http(s) URL');
      return;
    }
    await setDestinationUrl(normalized);
  }, [pasteValue, setDestinationUrl]);

  const handleBuildLandingPage = useCallback(async () => {
    setStatus('generating');
    setStatusText('');
    try {
      // Keep the current API signature (stylingUrl). pageKindHint is sanitized
      // and ready for use when/if we thread it into this call later.
      const generated = await generateTouchPage(api, campaignId, touch.id, undefined);
      const updatedTouch = generated?.touch || touch;
      onLocalUpdate?.(index, updatedTouch);
      setStatus('idle');
      setOpened(false);
    } catch (err) {
      setStatus('error');
      setStatusText('Draft failed - Retry');
      console.error(`[v2 messages] touch ${index} page generation failed:`, err);
    }
  }, [api, campaignId, touch, onLocalUpdate, index, pageKindHint]);

  const handleNoLinkNeeded = useCallback(async () => {
    const nextMessage = stripLinkToken(messageValue);
    const nextSms = stripLinkToken(touch.smsTemplate || '');
    setStatus('saving');
    setStatusText('');
    try {
      const updated = await patchTouch(api, campaignId, touch.id, {
        messageTemplate: nextMessage,
        smsTemplate: nextSms,
        destinationUrl: '',
      });
      onMessageChange(nextMessage);
      onLocalUpdate?.(index, updated);
      setStatus('idle');
      setOpened(false);
    } catch (err) {
      setStatus('error');
      setStatusText('Could not remove link token - Retry');
      console.error(`[v2 messages] touch ${index} no-link patch failed:`, err);
    }
  }, [api, campaignId, touch.id, touch.smsTemplate, messageValue, onLocalUpdate, onMessageChange, index]);

  const handleOpen = useCallback((nextOpened) => {
    setOpened(nextOpened);
    if (nextOpened) ensureDestinationsLoaded();
  }, [ensureDestinationsLoaded]);

  const recentUrls = destinations?.recentUrls || [];
  const hostedPages = destinations?.hostedPages || [];

  return (
    <Stack gap={6}>
      <Popover opened={opened} onChange={handleOpen} width={420} position="bottom-start" shadow="md">
        <Popover.Target>
          <Button
            size="xs"
            radius="xl"
            variant={selectedHostedPage || hasAviaryDestination ? 'light' : 'subtle'}
            color={chipTone}
            leftSection={status === 'generating'
              ? <Loader size={12} />
              : selectedHostedPage
                ? <IconSparkles size={14} />
                : hasSavedDestination
                  ? <IconWorld size={14} />
                  : <IconPlus size={14} />}
            disabled={status === 'saving' || status === 'generating'}
            onClick={() => {
              if (status === 'error') {
                handleBuildLandingPage();
                return;
              }
              setOpened((o) => !o);
              ensureDestinationsLoaded();
            }}
            style={{ paddingInline: 10, width: 'fit-content' }}
          >
            {chipText}
          </Button>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack gap="sm">
            <Text size="xs" fw={700} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.2 }}>
              DESTINATION
            </Text>

            {destinationsLoading && (
              <Group gap={8}>
                <Loader size={12} color="sage" />
                <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>Loading reusable destinations...</Text>
              </Group>
            )}

            {!destinationsLoading && destinationsError && (
              <Text size="sm" c="red">{destinationsError}</Text>
            )}

            {!destinationsLoading && !destinationsError && (
              <>
                {(hostedPages.length > 0 || recentUrls.length > 0) && (
                  <Stack gap={6}>
                    <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)' }}>Reuse one of my pages</Text>
                    {hostedPages.map((page) => (
                      <Button
                        key={`hosted-${page.url}`}
                        variant="subtle"
                        color="gray"
                        justify="flex-start"
                        leftSection={<IconSparkles size={14} />}
                        onClick={() => setDestinationUrl(page.url)}
                      >
                        {page.title || page.slug}
                      </Button>
                    ))}
                    {recentUrls.map((url) => (
                      <Button
                        key={`recent-${url}`}
                        variant="subtle"
                        color="gray"
                        justify="flex-start"
                        leftSection={
                          <img
                            src={`https://www.google.com/s2/favicons?sz=64&domain_url=${encodeURIComponent(url)}`}
                            alt=""
                            width={14}
                            height={14}
                          />
                        }
                        onClick={() => setDestinationUrl(url)}
                      >
                        {hostLabel(url)}
                      </Button>
                    ))}
                  </Stack>
                )}

                <Box>
                  <Divider my={6} />
                  <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)' }}>Paste a URL</Text>
                  <TextInput
                    mt={6}
                    size="xs"
                    placeholder="https://example.com/offer"
                    value={pasteValue}
                    onChange={(e) => setPasteValue(e.currentTarget.value)}
                    onBlur={handlePasteCommit}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handlePasteCommit();
                      }
                    }}
                  />
                </Box>

                <Divider my={2} />

                <Button
                  variant="light"
                  color="sage"
                  justify="flex-start"
                  leftSection={<IconSparkles size={14} />}
                  onClick={handleBuildLandingPage}
                >
                  <Stack gap={0} align="flex-start">
                    <Text size="sm" style={{ fontWeight: 500, color: 'inherit' }}>
                      Build me a landing page
                    </Text>
                    {pageKindHint && (
                      <Text c="dimmed" size="xs">
                        I&apos;d suggest a "{pageKindHint}"
                      </Text>
                    )}
                  </Stack>
                </Button>

                <Button
                  variant="subtle"
                  color="gray"
                  justify="flex-start"
                  leftSection={<IconLink size={14} />}
                  onClick={handleNoLinkNeeded}
                >
                  No link needed
                </Button>
              </>
            )}
          </Stack>
        </Popover.Dropdown>
      </Popover>

      {(selectedHostedPage || hasAviaryDestination) && (
        <Anchor href={currentUrl} target="_blank" rel="noreferrer" size="xs" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          Preview <IconExternalLink size={12} />
        </Anchor>
      )}
    </Stack>
  );
}

function TouchBubble({
  touch,
  index,
  total,
  day,
  channel,
  channelLabel,
  Icon,
  bubble,
  campaignId,
  onLocalUpdate,
  destinations,
  destinationsLoading,
  destinationsError,
  ensureDestinationsLoaded,
  noCustomOpener,
}) {
  const api = useApi();
  const initialMessage = String(touch.messageTemplate || '');
  const [value, setValue] = useState(initialMessage);
  const [status, setStatus] = useState((initialMessage || noCustomOpener) ? 'idle' : 'generating');
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved
  const generationStartedRef = useRef(false);
  const debounceRef = useRef(null);
  const lastSavedRef = useRef(initialMessage);

  useEffect(() => {
    setValue(String(touch.messageTemplate || ''));
    lastSavedRef.current = String(touch.messageTemplate || '');
  }, [touch.messageTemplate]);

  useEffect(() => {
    if (noCustomOpener) {
      generationStartedRef.current = false;
      setStatus('idle');
    }
  }, [noCustomOpener]);

  // Kick off generation once for empty touches.
  useEffect(() => {
    if (noCustomOpener) return;
    if (initialMessage) return;
    if (generationStartedRef.current) return;
    generationStartedRef.current = true;

    writeV2CampaignMessage(api, campaignId, index)
      .then(({ touch: updated }) => {
        const next = String(updated.messageTemplate || '');
        setValue(next);
        lastSavedRef.current = next;
        setStatus('idle');
        onLocalUpdate?.(index, updated);
      })
      .catch((err) => {
        setStatus('error');
        console.error(`[v2 messages] touch ${index} generation failed:`, err);
      });
  }, [api, campaignId, index, initialMessage, onLocalUpdate]);

  const commitSave = useCallback((next) => {
    if (next === lastSavedRef.current) return;
    setSaveState('saving');
    patchTouch(api, campaignId, touch.id, { messageTemplate: next })
      .then((updated) => {
        lastSavedRef.current = next;
        setSaveState('saved');
        onLocalUpdate?.(index, updated);
        setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 1500);
      })
      .catch((err) => {
        console.error(`[v2 messages] touch ${index} save failed:`, err);
        setSaveState('idle');
      });
  }, [api, campaignId, touch.id, index, onLocalUpdate]);

  function onChange(e) {
    const next = e.currentTarget.value;
    setValue(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => commitSave(next), DEBOUNCE_MS);
  }

  const role = roleForIndex(index, total, touch.title);
  const bubbleBg = bubble;
  const showBorder = bubbleBg === '#FFFFFF';
  const generating = status === 'generating';
  const hasError = status === 'error';
  const showDestinationChip = hasLinkToken(value);
  const showNotelessInvite = noCustomOpener && index === 0;

  return (
    <Stack gap={8}>
      <Group gap={8} align="center">
        <Badge color="sage" variant="light" radius="sm" size="sm">
          {formatDay(day)}
        </Badge>
        <Icon size={14} style={{ color: 'var(--aviary-muted)' }} />
        <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.3 }}>
          {channelLabel.toUpperCase()} · {role.toUpperCase()}
        </Text>
        <Box style={{ marginLeft: 'auto' }}>
          {saveState === 'saving' && (
            <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>Saving...</Text>
          )}
          {saveState === 'saved' && (
            <Group gap={4} align="center">
              <IconCheck size={12} style={{ color: 'var(--mantine-color-sage-6)' }} />
              <Text size="xs" style={{ color: 'var(--mantine-color-sage-6)' }}>Saved</Text>
            </Group>
          )}
        </Box>
      </Group>

      {generating && (
        <Box
          style={{
            background: bubbleBg,
            border: showBorder ? '1px solid var(--aviary-disabled)' : 'none',
            borderRadius: 16,
            borderTopLeftRadius: 4,
            padding: '14px 16px',
            maxWidth: 460,
            boxShadow: '0 1px 1px rgba(0, 0, 0, 0.04)',
          }}
        >
          <Stack gap={8}>
            <Skeleton height={12} radius="sm" />
            <Skeleton height={12} radius="sm" width="85%" />
            <Skeleton height={12} radius="sm" width="60%" />
          </Stack>
          <Group gap={6} mt={10} align="center">
            <Loader size={12} color="sage" />
            <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>Drafting...</Text>
          </Group>
        </Box>
      )}

      {hasError && (
        <Box
          style={{
            background: bubbleBg,
            border: '1px solid var(--mantine-color-red-3)',
            borderRadius: 16,
            borderTopLeftRadius: 4,
            padding: '14px 16px',
            maxWidth: 460,
          }}
        >
          <Text size="sm" c="red">Couldn't draft this one. Try again by refreshing.</Text>
        </Box>
      )}

      {!generating && !hasError && !showNotelessInvite && (
        <>
          <Textarea
            value={value}
            onChange={onChange}
            autosize
            minRows={3}
            styles={{
              input: {
                background: bubbleBg,
                border: showBorder ? '1px solid var(--aviary-disabled)' : '1px solid transparent',
                borderRadius: 16,
                borderTopLeftRadius: 4,
                padding: '14px 16px',
                fontSize: 14.5,
                lineHeight: 1.55,
                color: 'var(--aviary-ink)',
                maxWidth: 460,
                boxShadow: '0 1px 1px rgba(0, 0, 0, 0.04)',
              },
            }}
          />

          {/\{\{personalised_intro\}\}/.test(value) && (
            <Text size="xs" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic', maxWidth: 460 }}>
              This opener is personalised per prospect — the {'{{personalised_intro}}'} placeholder is filled in at send time from each prospect's LinkedIn profile.
            </Text>
          )}

          {showDestinationChip && (
            <DestinationChip
              touch={touch}
              campaignId={campaignId}
              index={index}
              messageValue={value}
              onMessageChange={setValue}
              onLocalUpdate={onLocalUpdate}
              destinations={destinations}
              destinationsLoading={destinationsLoading}
              destinationsError={destinationsError}
              ensureDestinationsLoaded={ensureDestinationsLoaded}
            />
          )}
        </>
      )}

      {showNotelessInvite && (
        <Box
          style={{
            background: 'var(--aviary-surface)',
            border: '1px solid var(--aviary-disabled)',
            borderRadius: 12,
            padding: '10px 12px',
            maxWidth: 460,
          }}
        >
          <Text size="sm" style={{ color: 'var(--aviary-ink)' }}>
            Noteless invite — LinkedIn&apos;s default &quot;Connect&quot; with no message
          </Text>
        </Box>
      )}
    </Stack>
  );
}

export default function MessagesScreen({
  touches = [],
  flow,
  answers,
  channel,
  campaignId,
  strategyNote,
  onTouchUpdate,
  customOpener = true,
  customOpenerSaving = false,
  onCustomOpenerChange,
}) {
  const api = useApi();
  const meta = channelMeta(channel);
  const { Icon, label: channelLabel, bubble } = meta;
  const [destinations, setDestinations] = useState(null);
  const [destinationsLoading, setDestinationsLoading] = useState(false);
  const [destinationsError, setDestinationsError] = useState('');

  const ensureDestinationsLoaded = useCallback(async () => {
    if (destinations || destinationsLoading) return;
    setDestinationsLoading(true);
    setDestinationsError('');
    try {
      const result = await fetchV2Destinations(api);
      setDestinations(result || { hostedPages: [], recentUrls: [] });
    } catch (err) {
      setDestinationsError(err.response?.data?.error || 'Failed to load destinations');
    } finally {
      setDestinationsLoading(false);
    }
  }, [api, destinations, destinationsLoading]);

  const timeline = useMemo(() => {
    let running = 0;
    return touches.map((touch) => {
      running += Number(touch.sendDayOffset || 0);
      return { touch, day: running };
    });
  }, [touches]);

  const totalDays = timeline.length ? timeline[timeline.length - 1].day : 0;
  const audience = audiencePhrase(flow, answers);
  const [expandedFollowUps, setExpandedFollowUps] = useState({});

  // Silently trigger generation of the hidden follow-up touches (indices 1+).
  // Their TouchBubble no longer renders here, so this effect is what keeps their
  // messageTemplate populated for later use (bot sends / V2.8 pipeline outreach).
  // write-message is idempotent server-side.
  const bgGenStartedRef = useRef(new Set());
  useEffect(() => {
    if (!campaignId) return;
    touches.forEach((touch, index) => {
      if (index === 0) return;
      if (String(touch.messageTemplate || '').trim()) return;
      const key = `${campaignId}:${index}`;
      if (bgGenStartedRef.current.has(key)) return;
      bgGenStartedRef.current.add(key);
      writeV2CampaignMessage(api, campaignId, index)
        .then(({ touch: updated }) => onTouchUpdate?.(index, updated))
        .catch(() => bgGenStartedRef.current.delete(key));
    });
  }, [api, campaignId, touches, onTouchUpdate]);

  return (
    <Stack gap="xl">
      <Stack gap={10}>
        <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 26, lineHeight: 1.25 }}>
          Here&apos;s your opener.
        </Text>
        {strategyNote && (
          <Text size="md" style={{ color: 'var(--aviary-ink)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {strategyNote}
          </Text>
        )}
        <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
          {touches.length > 0
            ? `${touches.length} ${channelLabel} ${touches.length === 1 ? 'message' : 'messages'}${totalDays > 0 ? ` over ${totalDays} days` : ''} for ${audience}. Edit any by hand.`
            : "I'll draft each message for you — edit anything by hand."}
        </Text>
      </Stack>

      {/*
        One outer card wraps the opener + explanation + follow-up rows to match the
        Wordsmith wireframe (2026-08-11). Touches 1+ still render as small collapsed
        rows with a click-to-expand "view" link that reveals the auto-written body.
        Full TouchBubble editing for touches 1+ is deferred — see Backlog.
      */}
      {timeline.length > 0 && (
        <Paper
          p="md"
          radius="md"
          withBorder
          style={{ background: '#fff', maxWidth: 560 }}
        >
          <Stack gap="md">
            <Group justify="space-between" align="baseline">
              <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
                OPENER · TEMPLATE
              </Text>
              <Text size="xs" c="sage" fw={600} style={{ fontStyle: 'italic' }}>
                edit by hand
              </Text>
            </Group>

            <Stack gap={4}>
              <Switch
                label="Send a personalised opening note"
                checked={customOpener}
                disabled={customOpenerSaving}
                onChange={(event) => onCustomOpenerChange?.(event.currentTarget.checked)}
                color="sage"
              />
              <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                Turn off to send LinkedIn&apos;s default noteless Connect invite. Saves your 5 custom-note quota for higher-intent prospects.
              </Text>
            </Stack>

            {timeline.slice(0, 1).map(({ touch, day }, index) => (
              <TouchBubble
                key={touch.id || index}
                touch={touch}
                index={index}
                total={touches.length}
                day={day}
                channel={channel}
                channelLabel={channelLabel}
                Icon={Icon}
                bubble={bubble}
                campaignId={campaignId}
                onLocalUpdate={onTouchUpdate}
                destinations={destinations}
                destinationsLoading={destinationsLoading}
                destinationsError={destinationsError}
                ensureDestinationsLoaded={ensureDestinationsLoaded}
                noCustomOpener={!customOpener && String(channel || '').toLowerCase() === 'linkedin'}
              />
            ))}

            {/* v2.0.2 — follow-up section removed. Follow-ups are still auto-drafted
                and stored on the campaign (bot / send worker uses them), but the coach
                doesn't see or edit them here. Kept simple: opener only in Wordsmith. */}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
